import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, LogOut, Upload, X, Package, ClipboardList, Settings2, Star, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";

const emptyProduct = { name: "", category: "injectable", substance: "", price: "", description: "", image_url: "", in_stock: true, featured: false };

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ brand_name: "", whatsapp_number: "" });
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/admin");
  }, [user, loading, navigate]);

  const loadAll = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/reviews/all").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const saveProduct = async () => {
    const payload = { ...editing, price: parseFloat(editing.price) || 0 };
    try {
      if (editing.id) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((p) => ({ ...p, image_url: data.image_url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/reviews/${id}`);
    toast.success("Review deleted");
    loadAll();
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    await api.delete(`/orders/${id}`);
    toast.success("Order deleted");
    loadAll();
  };

  const approveReview = async (id) => {
    await api.put(`/reviews/${id}/approve`);
    toast.success("Review approved & published");
    loadAll();
  };

  const uploadSettingsImage = async (field, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, [field]: data.image_url }));
      toast.success("Image uploaded — click Save settings to apply");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  if (loading || !user) return <div className="min-h-screen bg-[#050805]" />;

  const tabs = [
    { k: "products", label: "Products", icon: Package },
    { k: "orders", label: "Orders", icon: ClipboardList },
    { k: "reviews", label: "Reviews", icon: Star },
    { k: "settings", label: "Settings", icon: Settings2 },
  ];

  return (
    <div className="min-h-screen bg-[#050805]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-12 h-20 flex items-center justify-between sticky top-0 bg-[#050805]/90 backdrop-blur-xl z-40">
        <div className="flex items-center gap-8">
          <button onClick={() => navigate("/")} className="font-grotesk font-extrabold text-xl tracking-[0.3em]">LUXOR</button>
          <span className="font-body text-xs uppercase tracking-[0.3em] text-[#29FF87]">Admin</span>
        </div>
        <button onClick={() => { logout(); navigate("/admin"); }} className="flex items-center gap-2 text-zinc-400 hover:text-white font-body text-xs uppercase tracking-[0.2em]" data-testid="logout-button">
          <LogOut size={16} /> Logout
        </button>
      </header>

      <div className="px-6 md:px-12 py-10 max-w-[1600px] mx-auto">
        <div className="flex gap-2 mb-10 border-b border-white/10">
          {tabs.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-5 py-4 font-body text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === t.k ? "border-[#29FF87] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
              data-testid={`tab-${t.k}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-grotesk text-3xl">Products <span className="text-zinc-600 text-xl">({products.length})</span></h2>
              <button onClick={() => setEditing({ ...emptyProduct })} className="flex items-center gap-2 bg-[#29FF87] text-[#050805] px-5 py-3 font-body text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#5CFFA8] transition-colors" data-testid="add-product-button">
                <Plus size={16} /> New product
              </button>
            </div>
            <div className="border border-white/10">
              {products.map((p) => (
                <div key={p.id} className="grid grid-cols-12 items-center gap-4 px-4 py-4 border-b border-white/10 last:border-0 hover:bg-white/5 transition-colors" data-testid={`admin-product-${p.id}`}>
                  <div className="col-span-1"><img src={resolveImage(p.image_url)} alt="" className="w-12 h-12 object-cover border border-white/10" /></div>
                  <div className="col-span-4"><p className="font-grotesk font-semibold text-sm">{p.name}</p><p className="font-body text-xs text-zinc-500">{p.substance}</p></div>
                  <div className="col-span-2"><span className="font-body text-[10px] uppercase tracking-[0.2em] text-[#29FF87]">{p.category}</span></div>
                  <div className="col-span-2 font-grotesk text-xl">€{Number(p.price).toFixed(2)}</div>
                  <div className="col-span-2 font-body text-xs text-zinc-500">{p.in_stock ? "In stock" : "Sold out"}{p.featured ? " · Featured" : ""}</div>
                  <div className="col-span-1 flex gap-3 justify-end">
                    <button onClick={() => setEditing({ ...p, price: String(p.price) })} className="text-zinc-400 hover:text-[#29FF87]" data-testid={`edit-${p.id}`}><Pencil size={16} /></button>
                    <button onClick={() => deleteProduct(p.id)} className="text-zinc-400 hover:text-red-500" data-testid={`delete-${p.id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div>
            <h2 className="font-grotesk text-3xl mb-8">Orders <span className="text-zinc-600 text-xl">({orders.length})</span></h2>
            {orders.length === 0 ? <p className="font-body text-zinc-600 text-sm">No orders yet.</p> : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="border border-white/10 p-6" data-testid={`admin-order-${o.id}`}>
                    <div className="flex justify-between flex-wrap gap-4">
                      <div>
                        <p className="font-grotesk font-bold text-white">{o.first_name} {o.last_name} <span className="text-zinc-500 font-normal">· {o.phone}</span></p>
                        <p className="font-body text-sm text-[#7BC79A] mt-1">{o.email}</p>
                        <p className="font-body text-sm text-zinc-500 mt-1">{o.street} {o.house_no}{o.apartment ? ", " + o.apartment : ""}, {o.postal_code} {o.city}{o.region ? ", " + o.region : ""}, {o.country}</p>
                      </div>
                      <div className="flex items-start gap-4">
                        <span className="font-grotesk text-2xl text-[#29FF87]">€{Number(o.total).toFixed(2)}</span>
                        <button onClick={() => deleteOrder(o.id)} className="text-zinc-400 hover:text-red-500" data-testid={`delete-order-${o.id}`} title="Delete order"><Trash2 size={18} /></button>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10 font-body text-sm text-zinc-400 space-y-1">
                      {o.items.map((i, idx) => <p key={idx}>{i.name} × {i.quantity} — €{(i.price * i.quantity).toFixed(2)}</p>)}
                    </div>
                    {o.notes && <p className="mt-3 font-body text-xs text-zinc-500 italic">Note: {o.notes}</p>}
                    <p className="mt-3 font-body text-[10px] uppercase tracking-[0.2em] text-zinc-600">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "reviews" && (
          <div>
            <h2 className="font-grotesk text-3xl mb-8">Reviews <span className="text-zinc-600 text-xl">({reviews.length})</span></h2>
            {reviews.length === 0 ? <p className="font-body text-zinc-600 text-sm">No reviews yet.</p> : (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r.id} className={`border p-6 flex justify-between gap-4 ${r.approved ? "border-white/10" : "border-[#29FF87]/40 bg-[#29FF87]/5"}`} data-testid={`admin-review-${r.id}`}>
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map((s) => <Star key={s} size={13} fill={s <= r.rating ? "#29FF87" : "transparent"} stroke="#29FF87" strokeWidth={1.5} />)}
                        </div>
                        <p className="font-grotesk font-bold text-white">{r.name}</p>
                        {!r.approved && <span className="text-[10px] uppercase tracking-[0.2em] text-[#050805] bg-[#29FF87] px-2 py-0.5 rounded-full font-bold">Pending</span>}
                      </div>
                      <p className="font-body text-sm text-zinc-400 mt-2">"{r.text}"</p>
                      <p className="mt-3 font-body text-[10px] uppercase tracking-[0.2em] text-zinc-600">{new Date(r.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {!r.approved && (
                        <button onClick={() => approveReview(r.id)} className="flex items-center gap-1 text-[#29FF87] hover:text-[#5CFFA8]" data-testid={`approve-review-${r.id}`} title="Approve"><Check size={18} /></button>
                      )}
                      <button onClick={() => deleteReview(r.id)} className="text-zinc-400 hover:text-red-500" data-testid={`delete-review-${r.id}`}><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-2xl">
            <h2 className="font-grotesk text-3xl mb-8">Settings</h2>
            <div className="space-y-8">
              <div>
                <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Brand name</label>
                <input value={settings.brand_name || ""} onChange={(e) => setSettings({ ...settings, brand_name: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#29FF87] outline-none py-3 font-body text-sm transition-colors" data-testid="settings-brand" />
              </div>
              <div>
                <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">WhatsApp number (with country code, no +)</label>
                <input value={settings.whatsapp_number || ""} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                  placeholder="4915510160416"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#29FF87] outline-none py-3 font-body text-sm transition-colors" data-testid="settings-whatsapp" />
              </div>
              <div>
                <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Telegram link (full URL)</label>
                <input value={settings.telegram_link || ""} onChange={(e) => setSettings({ ...settings, telegram_link: e.target.value })}
                  placeholder="https://t.me/yourchannel"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#29FF87] outline-none py-3 font-body text-sm transition-colors" data-testid="settings-telegram" />
              </div>
              <div>
                <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Top bar text (green banner)</label>
                <input value={settings.announcement_text || ""} onChange={(e) => setSettings({ ...settings, announcement_text: e.target.value })}
                  placeholder="Free shipping over €200 · Lab-verified · Order via WhatsApp"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#29FF87] outline-none py-3 font-body text-sm transition-colors" data-testid="settings-announcement" />
              </div>
              <div>
                <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Footer description</label>
                <textarea value={settings.site_description || ""} onChange={(e) => setSettings({ ...settings, site_description: e.target.value })} rows={2}
                  placeholder="Pharmaceutical-grade performance compounds..."
                  className="w-full bg-transparent border border-white/20 focus:border-[#29FF87] outline-none p-3 font-body text-sm resize-none mt-2 transition-colors" data-testid="settings-description" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-2">
                {[
                  { field: "logo_image", label: "Logo", testid: "logo" },
                  { field: "injectable_image", label: "Injectable image", testid: "injectable" },
                  { field: "oral_image", label: "Oral image", testid: "oral" },
                ].map((img) => (
                  <div key={img.field}>
                    <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500 block mb-2">{img.label}</label>
                    <div className="aspect-square w-full border border-white/15 rounded-lg overflow-hidden bg-[#0B120B] mb-3">
                      {settings[img.field] && <img src={resolveImage(settings[img.field])} alt={img.label} className="w-full h-full object-cover" />}
                    </div>
                    <label className="flex items-center justify-center gap-2 border border-white/20 hover:border-[#29FF87] px-3 py-2 cursor-pointer font-body text-[10px] uppercase tracking-[0.2em] transition-colors rounded-md">
                      <Upload size={13} /> Upload
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSettingsImage(img.field, e)} data-testid={`settings-upload-${img.testid}`} />
                    </label>
                  </div>
                ))}
              </div>

              <button onClick={saveSettings} className="bg-[#29FF87] text-[#050805] px-8 py-3 font-body text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#5CFFA8] transition-colors" data-testid="save-settings">Save settings</button>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="product-modal">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0B120B] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-5 border-b border-white/10 sticky top-0 bg-[#0B120B]">
              <h3 className="font-grotesk font-bold text-lg tracking-[0.2em] uppercase">{editing.id ? "Edit product" : "New product"}</h3>
              <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white" data-testid="close-modal"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Name</label>
                  <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-transparent border-b border-white/20 focus:border-[#29FF87] outline-none py-2 text-sm" data-testid="form-name" />
                </div>
                <div>
                  <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Category</label>
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full bg-[#0B120B] border-b border-white/20 focus:border-[#29FF87] outline-none py-2 text-sm" data-testid="form-category">
                    <option value="injectable">Injectable</option>
                    <option value="oral">Oral</option>
                  </select>
                </div>
                <div>
                  <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Price (€)</label>
                  <input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="w-full bg-transparent border-b border-white/20 focus:border-[#29FF87] outline-none py-2 text-sm" data-testid="form-price" />
                </div>
                <div className="col-span-2">
                  <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Substance</label>
                  <input value={editing.substance} onChange={(e) => setEditing({ ...editing, substance: e.target.value })} className="w-full bg-transparent border-b border-white/20 focus:border-[#29FF87] outline-none py-2 text-sm" data-testid="form-substance" />
                </div>
                <div className="col-span-2">
                  <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">Description</label>
                  <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="w-full bg-transparent border border-white/20 focus:border-[#29FF87] outline-none p-3 text-sm resize-none mt-2" data-testid="form-description" />
                </div>
                <div className="col-span-2">
                  <label className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500 block mb-2">Image</label>
                  <div className="flex items-center gap-4">
                    {editing.image_url && <img src={resolveImage(editing.image_url)} alt="" className="w-20 h-20 object-cover border border-white/10" />}
                    <label className="flex items-center gap-2 border border-white/20 hover:border-[#29FF87] px-4 py-3 cursor-pointer font-body text-xs uppercase tracking-[0.2em] transition-colors">
                      <Upload size={14} /> {uploading ? "Uploading..." : "Upload image"}
                      <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="form-image-upload" />
                    </label>
                  </div>
                  <input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="or paste image URL" className="w-full bg-transparent border-b border-white/20 focus:border-[#29FF87] outline-none py-2 text-sm mt-4" data-testid="form-image-url" />
                </div>
                <div className="flex gap-8 col-span-2">
                  <label className="flex items-center gap-2 font-body text-sm text-zinc-400 cursor-pointer">
                    <input type="checkbox" checked={editing.in_stock} onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })} data-testid="form-instock" /> In stock
                  </label>
                  <label className="flex items-center gap-2 font-body text-sm text-zinc-400 cursor-pointer">
                    <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="form-featured" /> Featured
                  </label>
                </div>
              </div>
              <button onClick={saveProduct} className="w-full bg-[#29FF87] text-[#050805] py-4 font-body text-sm uppercase tracking-[0.25em] font-semibold hover:bg-[#5CFFA8] transition-colors" data-testid="save-product">
                {editing.id ? "Update product" : "Create product"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
